import { type BrandInsightsPageContext, type ChatContextItem, ChatContextType } from '@trinity/chat';
import type { ChatState } from '../../Chat.State/interfaces';

export type OpenNewChatForSubjectPayload = {
  subject: {
    id: number | string;
    name: string;
    kind?: string;
    link?: string;
  };
  chatContextIdentifier: string;
  pendingQuery?: string | null;
};

type CreateOpenNewChatForSubjectStateActionParams = {
  brandIdentifier: string;
  contextItems: ChatContextItem[];
  payload: OpenNewChatForSubjectPayload;
};

function mapContextItemToPageContext(item: ChatContextItem): BrandInsightsPageContext.Item {
  if (item.agent.identifier === (ChatContextType.Global as string)) {
    return {
      type: ChatContextType.Global,
    };
  }

  return {
    type: ChatContextType.Agent,
    data: item,
    metadata: {},
  };
}

function resolveOpenNewChatContext(
  contextItems: ChatContextItem[],
  payload: {
    subject: OpenNewChatForSubjectPayload['subject'];
    chatContextIdentifier: string;
  }
): BrandInsightsPageContext.Item | null {
  const selectedContextItem = contextItems.find((item) => item.identifier === payload.chatContextIdentifier);

  if (!selectedContextItem) {
    return null;
  }

  const baseContext = mapContextItemToPageContext(selectedContextItem);

  if (baseContext.type !== ChatContextType.Agent) {
    return baseContext;
  }

  if (typeof baseContext.data !== 'object' || baseContext.data === null) {
    return null;
  }

  return {
    ...baseContext,
    data: {
      ...baseContext.data,
      subject: payload.subject,
    },
  };
}

export function createOpenNewChatForSubjectStateAction(
  params: CreateOpenNewChatForSubjectStateActionParams
): ChatState.Action | null {
  const context = resolveOpenNewChatContext(params.contextItems, params.payload);
  if (!context) {
    return null;
  }

  return {
    type: 'new-chat-started',
    payload: {
      brandIdentifier: params.brandIdentifier,
      context,
      pendingQuery: params.payload.pendingQuery ?? null,
    },
  };
}
